import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const OrgContext = createContext(null);

export const OrgProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrgData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch organization details
      const orgResponse = await api.get('/organizations/me');

      if (orgResponse.data.success) {
        const orgData = orgResponse.data.data;
        setOrganization(orgData);
        setPlan(orgData.planId);

        // Calculate usage from organization
        setUsage(orgData.usage || {
          users: 1,
          calls: 0,
          storage: 0,
          contacts: 0,
          campaigns: 0
        });
      }
    } catch (error) {
      // If we get a 403 with subscriptionExpired, extract the data from the error response
      if (error.response?.status === 403 && error.response?.data?.subscriptionExpired) {
        const data = error.response.data;

        // If the organization no longer exists, force logout
        if (data.reason === 'org_deleted') {
          localStorage.removeItem('admin_auth_token');
          localStorage.removeItem('admin_user_data');
          window.location.href = '/login';
          return;
        }

        setOrganization(prev => ({
          ...prev,
          subscriptionExpired: true,
          expireReason: data.reason || 'unknown',
          status: data.reason === 'suspended' ? 'suspended' :
                  data.reason === 'cancelled' ? 'cancelled' :
                  data.reason === 'trial_expired' ? 'trial' :
                  data.reason === 'subscription_expired' ? 'active' : prev?.status,
          trialEndsAt: data.trialEndsAt || prev?.trialEndsAt,
          subscriptionEndsAt: data.subscriptionEndsAt || prev?.subscriptionEndsAt,
        }));
      }
      console.error('Error fetching organization data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrgData();
    } else {
      setOrganization(null);
      setPlan(null);
      setUsage(null);
      setLoading(false);
    }
  }, [isAuthenticated, fetchOrgData]);

  // Listen for subscription expired events from the API interceptor
  useEffect(() => {
    const handleSubscriptionExpired = (event) => {
      const { reason, trialEndsAt, subscriptionEndsAt } = event.detail;

      // If the organization no longer exists, force logout
      if (reason === 'org_deleted') {
        localStorage.removeItem('admin_auth_token');
        localStorage.removeItem('admin_user_data');
        window.location.href = '/login';
        return;
      }

      setOrganization(prev => ({
        ...prev,
        subscriptionExpired: true,
        expireReason: reason || 'unknown',
        status: reason === 'suspended' ? 'suspended' :
                reason === 'cancelled' ? 'cancelled' :
                reason === 'trial_expired' ? 'trial' :
                reason === 'subscription_expired' ? 'active' : prev?.status,
        trialEndsAt: trialEndsAt || prev?.trialEndsAt,
        subscriptionEndsAt: subscriptionEndsAt || prev?.subscriptionEndsAt,
      }));
    };

    const handleSubscriptionUpdated = () => {
      // Refresh org data when subscription is activated/renewed via payment
      fetchOrgData();
    };

    window.addEventListener('subscriptionExpired', handleSubscriptionExpired);
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdated);
    return () => {
      window.removeEventListener('subscriptionExpired', handleSubscriptionExpired);
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdated);
    };
  }, [fetchOrgData]);

  const canUseFeature = (featureName) => {
    if (!plan?.features) return false;
    return plan.features[featureName] === true;
  };

  const checkLimit = (resource) => {
    if (!usage || !plan) {
      return { allowed: true, current: 0, limit: Infinity, remaining: Infinity };
    }

    const current = usage[resource] || 0;
    let limit = plan.limits?.[resource] ?? Infinity;

    // For per-user plans, the user limit is enforced by paidSeats.
    // paidSeats covers telecallers/managers; the org admin is always free (+1).
    if (resource === 'users' && plan.isPerUser && organization?.paidSeats) {
      limit = organization.paidSeats + 1;
    }

    // -1 means unlimited
    const isUnlimited = limit === -1 || limit === Infinity;

    return {
      allowed: isUnlimited || current < limit,
      current,
      limit: isUnlimited ? 'unlimited' : limit,
      remaining: isUnlimited ? 'unlimited' : Math.max(0, limit - current),
      percentage: isUnlimited ? 0 : (current / limit) * 100
    };
  };

  const refreshOrg = () => {
    return fetchOrgData();
  };

  // Compute subscription expiration status from organization data
  // The /api/organizations/me endpoint already returns subscriptionExpired and expireReason
  const isExpired = organization?.subscriptionExpired === true;
  const expireReason = organization?.expireReason || null;

  // Calculate days remaining until subscription expiry.
  // null when no end date is recorded (nothing to count down) — prevents
  // consumers from showing a fake "expires today" notice
  const getDaysRemaining = () => {
    if (!organization) return null;
    const endDate = organization.subscriptionEndsAt || organization.trialEndsAt;
    if (!endDate) return null;
    return Math.max(0, Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24)));
  };

  const daysRemaining = getDaysRemaining();

  const value = {
    organization,
    plan,
    usage,
    loading,
    canUseFeature,
    checkLimit,
    refreshOrg,
    // Organization actions
    isTrial: organization?.status === 'trial',
    isActive: organization?.status === 'active',
    isSuspended: organization?.status === 'suspended',
    trialDaysRemaining: organization?.trialEndsAt
      ? Math.max(0, Math.ceil((new Date(organization.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
      : 0,
    // Subscription expiration
    isExpired,
    expireReason,
    daysRemaining,
    subscriptionEndsAt: organization?.subscriptionEndsAt || null,
    subscriptionStartsAt: organization?.subscriptionStartsAt || null,
    trialEndsAt: organization?.trialEndsAt || null,
    billingCycle: organization?.billingCycle || null,
    paidSeats: organization?.paidSeats || 1,
    pendingSeatsChange: organization?.pendingSeatsChange ?? null,
    pendingDowngradePlan: organization?.pendingDowngradePlan ?? null,
    pendingBillingCycle: organization?.pendingBillingCycle ?? null,
    autopay: organization?.autopay || null,
    orgState: organization?.address?.state || null,
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

export default OrgContext;